import { Nullable } from "@types";
import {
  ChangeEvent,
  DetailedHTMLProps,
  Dispatch,
  InputHTMLAttributes,
  RefObject,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";

export interface UseInputParams {
  initialValue?: string;
  placeholder?: string;
  inputValidator?: (str: string) => boolean;
  submitValidator?: (str: string, compareValue?: string) => boolean;
  compareValue?: string;
  inputAttribute?: DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;
}

export interface UseInputReturn {
  attribute: DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;
  ref: RefObject<HTMLInputElement>;
  file: null | string | ArrayBuffer;
  value: string;
  setValue: Dispatch<SetStateAction<string>>;
  isValidInput: boolean;
  isValidSubmit: boolean;
}

export const useInput = ({
  initialValue = "",
  placeholder = "",
  inputValidator = () => true,
  submitValidator = () => true,
  inputAttribute = {},
  compareValue = "",
}: UseInputParams = {}): UseInputReturn => {
  const ref = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<Nullable<string | ArrayBuffer>>(null);
  const [value, setValue] = useState<string>(initialValue);
  const [isValidInput, setIsValidInput] = useState(true);
  const [isValidSubmit, setIsValidSubmit] = useState(true);

  useEffect(() => {
    if (submitValidator(value, compareValue) || value.length === 0) {
      setIsValidSubmit(true);
    } else {
      setIsValidSubmit(false);
    }
  }, [compareValue, value]);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const onChange = async (event: ChangeEvent<HTMLInputElement>) => {
    /* file */
    if (event.target.files) {
      const file = event.target.files[0];
      const base64: Nullable<string | ArrayBuffer> = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
      setFile(base64);
      return;
    }

    /* others */
    if (inputValidator(event.target.value)) {
      setValue(event.target.value);
    } else {
      if (!isValidInput) return;
      setIsValidInput(false);
      const nodeTimer: NodeJS.Timeout = setTimeout(() => {
        setIsValidInput(true);
        return clearTimeout(nodeTimer);
      }, 1000);
    }
  };

  return {
    attribute: {
      ref,
      value,
      placeholder,
      onChange,
      ...inputAttribute,
    },
    ref,
    file,
    value,
    setValue,
    isValidInput,
    isValidSubmit,
  };
};
